import { FILE_TYPES, ONEDRIVE_FILE_TYPES, FILE_STATE, STATE } from "../redux/constants/metaData";
import { isValidPlayload } from "./reduxMethod";
import { OAS365ICONS } from "../icons";

export const getValidValue = (value, defaultValue) => {
  return value === null
    ? defaultValue
    : value === undefined
      ? defaultValue
      : value === ""
        ? defaultValue
        : value;
};

export const sanitizeFileName = (fileName) => {
  if (!fileName || typeof fileName !== "string") {
    return "unnamed_file_" + Date.now();
  }

  const lastDotIndex = fileName.lastIndexOf(".");
  let name = fileName;
  let extension = "";

  if (lastDotIndex > 0) {
    name = fileName.substring(0, lastDotIndex);
    extension = fileName.substring(lastDotIndex);
  }

  const sanitizedName = name.replace(/[^\x00-\x7FæøåäöÆØÅÄÖ]/g, "");

  const alphanumericOnly = sanitizedName.replace(/[^a-zA-Z0-9æøåäöÆØÅÄÖ]/g, "");

  if (alphanumericOnly.length === 0) {
    return "document_" + Date.now() + extension;
  }

  return sanitizedName + extension;
};

export const getDoubleDigit = (num) => {
  if (num >= 10) {
    return `${num}`;
  } else {
    return `0${num}`;
  }
};

export const messageText = {
  failedToDownload: "Document failed to download",
  serverResponseError: "Timeout! Server failed to response",
};

export const getFileExtension = (fileName) => {
  if (fileName !== null && fileName !== "") {
    let ext = fileName;
    ext = ext.split(".").pop().toUpperCase();
    return ext;
  }
  return "";
};

export const getFileName = (fileName) => {
  if (fileName !== null && fileName !== "") {
    let title = fileName;
    title = title.replace(/\.[^/.]+$/, "");
    return title;
  }
  return "";
};

/** Decode URL-encoded filename for display (e.g. 11%2F06%2F2025 → 11/06/2025). Safe if not encoded. */
export const getDisplayFileName = (fileName) => {
  if (fileName == null || fileName === "") return fileName ?? "";
  try {
    return decodeURIComponent(String(fileName));
  } catch {
    return fileName;
  }
};

/**
 * Returns the doc class code only (strips " - DocClassName" suffix).
 * Backend may return DocClass as "CODE - Name"; APIs expect only CODE for checkout/check-in.
 * @param {string} docClass - Value from API or UI (e.g. "01-TEST - Testing 1.1" or "01-TEST")
 * @returns {string} Class code only (e.g. "01-TEST")
 */
export const getDocClassCodeOnly = (docClass) => {
  if (docClass == null || typeof docClass !== "string") return "";
  const s = docClass.trim();
  const idx = s.indexOf(" - ");
  return idx > 0 ? s.substring(0, idx).trim() : s;
};

export const getTitle = (fileName) => {
  if (!fileName || typeof fileName !== "string") return fileName ?? "";
  let decoded = fileName;
  try {
    decoded = decodeURIComponent(fileName);
  } catch {
    decoded = fileName;
  }
  const nameWithoutExt = decoded.replace(/\.[^/.]+$/, "");
  const cleanedName = nameWithoutExt.replace(/\s*\([^)]*\)\s*-\s*\d+$/, "");
  return cleanedName.trim();
};

export const getFileTypeExtension = (fileType) => {
  switch (fileType) {
    case ONEDRIVE_FILE_TYPES.docx:
      return FILE_TYPES.docx;
    case ONEDRIVE_FILE_TYPES.xlsx:
      return FILE_TYPES.xlsx;
    case ONEDRIVE_FILE_TYPES.pptx:
      return FILE_TYPES.pptx;
    case ONEDRIVE_FILE_TYPES.pdf:
      return FILE_TYPES.pdf;
    case ONEDRIVE_FILE_TYPES.allFiles:
    default:
      return FILE_TYPES.allFiles;
  }
};

export const isFileExtValid = (docFileTypeList, uploadFileList, index = 0) => {
  if (isValidPlayload(uploadFileList) && uploadFileList?.length >= index) {
    const file = uploadFileList[index];
    if (isValidPlayload(file) && isValidPlayload) {
      const ext = getFileExtension(file.name);
      return isValidFileType(docFileTypeList, ext);
    }
  }

  return false;
};

export const isValidFileType = (docFileTypeList, ext, docType = "") => {
  let isValid = false;
  if (
    docFileTypeList !== undefined &&
    docFileTypeList !== null &&
    docFileTypeList.length > 0
  ) {
    if (ext !== null && ext !== "") {
      docFileTypeList.map((item) => {
        if (docType === "") {
          if (item.fileExtention.toUpperCase() === ext.toUpperCase()) {
            isValid = true;
          }
        } else {
          if (item.documentType !== null && item.documentType !== "") {
            if (
              item.documentType.toUpperCase() === docType.toUpperCase() &&
              item.fileExtention.toUpperCase() === ext.toUpperCase()
            ) {
              isValid = true;
            }
          } else {
            isValid = true;
          }
        }
      });
    } else {
      isValid = false;
    }
  } else {
    isValid = true;
  }

  return isValid;
};

export const isValidPDFFileType = (ext, viewCopyType) => {
  return ext.toUpperCase() === viewCopyType;
};

function parseRowMetaDataObject(row) {
  const raw = row?.MetaData ?? row?.metaData ?? row?.metadata;
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function pickFirstNonEmptyString(...candidates) {
  for (const value of candidates) {
    if (value == null) continue;
    const text = String(value).trim();
    if (text !== "") return text;
  }
  return "";
}


export function isWorkingDocumentDownloaded(file) {
  if (!file) return false;
  const meta = parseRowMetaDataObject(file) ?? {};
  return (
    meta.IsDownloaded === true ||
    meta.isDownloaded === true ||
    file.IsDownloaded === true ||
    file.isDownloaded === true
  );
}

export function isWorkingDocumentCheckedOut(file) {
  if (!file) return false;
  const meta = parseRowMetaDataObject(file) ?? {};

  if (meta.IsDownloaded === true || meta.isDownloaded === true) {
    return false;
  }

  const explicitState = pickFirstNonEmptyString(
    file.fileState,
    file.FileState,
    meta.fileState,
    meta.FileState,
  );
  if (explicitState === FILE_STATE.checkedOut) return true;
  if (explicitState === FILE_STATE.checkedIn) return false;

  const lastAction = pickFirstNonEmptyString(
    file.LastAction,
    file.lastAction,
    meta.LastAction,
    meta.lastAction,
  );
  if (lastAction) {
    const norm = lastAction.toLowerCase();
    if (norm.includes("undo check out") || norm.includes("undo check-out")) {
      return false;
    }
    if (/\bchecked[-\s]?out\b/i.test(lastAction)) {
      return true;
    }
  }

  const rawHistory = file.latestHistory ?? file.LatestHistory ?? meta.latestHistory ?? meta.LatestHistory;
  let history = null;
  if (rawHistory) {
    if (typeof rawHistory === "string") {
      try {
        const parsed = JSON.parse(rawHistory);
        history = Array.isArray(parsed) ? parsed[0] : parsed;
      } catch {
        history = null;
      }
    } else if (Array.isArray(rawHistory)) {
      history = rawHistory[0] ?? null;
    } else if (typeof rawHistory === "object") {
      history = rawHistory;
    }
  }
  const historyCategory = pickFirstNonEmptyString(
    history?.InfoCategory,
    history?.infoCategory,
    history?.Category,
    history?.category,
  );
  if (historyCategory) {
    const norm = historyCategory.toLowerCase();
    if (norm.includes("undo check out") || norm.includes("undo check-out")) {
      return false;
    }
    if (/\bchecked[-\s]?out\b/i.test(historyCategory)) {
      return true;
    }
  }

  if (
    pickFirstNonEmptyString(
      file.CurrentlyCheckedOutDate,
      file.currentlyCheckedOutDate,
      meta.CurrentlyCheckedOutDate,
      meta.currentlyCheckedOutDate,
    ) !== ""
  ) {
    return true;
  }

  return false;
}

export function isDocumentReleased(file) {
  if (!file) return false;
  const meta = parseRowMetaDataObject(file) ?? {};

  const statusOrState = pickFirstNonEmptyString(
    file.Status,
    file.status,
    file.State,
    file.state,
    meta.State,
    meta.state,
    meta.Status,
    meta.status,
  );
  if (statusOrState === STATE.released) return true;

  const lastAction = pickFirstNonEmptyString(
    file.LastAction,
    file.lastAction,
    meta.LastAction,
    meta.lastAction,
  );
  if (lastAction && /\breleased\b/i.test(lastAction)) return true;

  return false;
}

export const getFileIconPath = (fileType, row) => {
  const actualFileType = row?.FileType || fileType || "";
  const ft = String(actualFileType).toLowerCase();
  const isCheckedOut = isWorkingDocumentCheckedOut(row);
  const isReleased = isDocumentReleased(row);

  const normalizeExt = (v) =>
    String(v || "")
      .trim()
      .toLowerCase()
      .replace(/^\./, "");
  const extractExtFromString = (v) => {
    const s = String(v || "")
      .trim()
      .toLowerCase();
    if (!s) return "";
    if (/^[a-z0-9]{1,8}$/.test(s)) return s;
    if (/^\.[a-z0-9]{1,8}$/.test(s)) return s.slice(1);
    const m = s.match(/\.([a-z0-9]{1,8})(?:$|[?#])/);
    return m?.[1] || "";
  };

  const ext =
    normalizeExt(row?.FileExtension) ||
    extractExtFromString(row?.EdmFileForFileRef?.fileName) ||
    extractExtFromString(actualFileType);

  const wordExts = ["doc", "docx", "docm", "dot", "dotx", "dotm", "rtf", "odt"];
  const excelExts = ["xls", "xlsx", "xlsm", "xlsb", "xlt", "xltx", "xltm", "ods", "csv"];
  const powerpointExts = ["ppt", "pptx", "pptm", "pot", "potx", "potm", "pps", "ppsx", "ppsm"];

  const isWord =
    ft.includes("word") ||
    ft.includes("application/msword") ||
    ft.includes("application/vnd.ms-word") ||
    ft.includes("application/vnd.openxmlformats-officedocument.wordprocessingml") ||
    ft.includes("wordprocessingml") ||
    ft.includes("application/vnd.oasis.opendocument.text") ||
    ft.includes("application/rtf") ||
    ft.includes("text/rtf") ||
    wordExts.includes(ext);

  const isExcelOrCsv =
    ft.includes("excel") ||
    ft.includes("spreadsheet") ||
    ft.includes("application/vnd.ms-excel") ||
    ft.includes("application/vnd.openxmlformats-officedocument.spreadsheetml") ||
    ft.includes("spreadsheetml") ||
    ft.includes("application/vnd.oasis.opendocument.spreadsheet") ||
    ft.includes("text/csv") ||
    ft.includes("application/csv") ||
    ft.includes("text/comma-separated-values") ||
    ft.includes("csv") ||
    excelExts.includes(ext);

  const isPowerPoint =
    ft.includes("powerpoint") ||
    ft.includes("presentation") ||
    ft.includes("application/vnd.ms-powerpoint") ||
    ft.includes("application/vnd.openxmlformats-officedocument.presentationml") ||
    ft.includes("presentationml") ||
    ft.includes("application/vnd.oasis.opendocument.presentation") ||
    powerpointExts.includes(ext);

  if (isWord && isReleased) return OAS365ICONS.wordReleasedIcon;
  if (isWord && isCheckedOut) return OAS365ICONS.wordLockIcon;
  if (isWord) return OAS365ICONS.wordIcon;

  if (isExcelOrCsv && isReleased) return OAS365ICONS.excelReleasedIcon;
  if (isExcelOrCsv && isCheckedOut) return OAS365ICONS.excelLockIcon;
  if (isExcelOrCsv) return OAS365ICONS.excelIcon;

  if (isPowerPoint && isReleased) return OAS365ICONS.powerpointReleasedIcon;
  if (isPowerPoint && isCheckedOut) return OAS365ICONS.powerpointLockIcon;
  if (isPowerPoint) return OAS365ICONS.powerpoint;

  const isImage =
    ft.includes("image/") ||
    ft.includes("image") ||
    ["jpg", "jpeg", "png", "gif", "tif", "tiff", "bmp", "webp"].includes(ext);
  if (isImage && isReleased) return OAS365ICONS.imageReleasedIcon;
  if (isImage && isCheckedOut) return OAS365ICONS.imageLockIcon;
  if (isImage) return OAS365ICONS.imageIcon;

  const isPdf =
    ft.includes("pdf") || ft.includes("acrobat") || ft.includes("application/pdf");

  if (isPdf && isReleased) return OAS365ICONS.pdfReleasedIcon;
  if (isPdf && isCheckedOut) return OAS365ICONS.pdfLockIcon;
  if (isPdf) return OAS365ICONS.pdfIcon;

  const isOutlook =
    ft.includes("email") ||
    ft.includes("e-mail") ||
    ft.includes("message/rfc822") ||
    ft.includes("application/vnd.ms-outlook") ||
    ft.includes("application/x-msg") ||
    ft.includes("msg") ||
    ft.includes("eml") ||
    ft.includes("application/eml");

  if (isOutlook && isReleased) return OAS365ICONS.outlookReleasedIcon;
  if (isOutlook && isCheckedOut) return OAS365ICONS.outlookLockIcon;
  if (isOutlook) return OAS365ICONS.outlook;

  const isText =
    ft.includes("txt") ||
    ft.includes("textfile") ||
    ft === "text" ||
    ft.includes("text/plain") ||
    ft.includes("text/html") ||
    ft.includes("application/xhtml+xml") ||
    ft.includes("html") ||
    ft.includes("text/json") ||
    ft.includes("application/json") ||
    ft.includes("json") ||
    ft.includes("text/xml") ||
    ft.includes("application/xml") ||
    ft.includes("application/xml-dtd") ||
    ft.includes("xml") ||
    ft.includes("text/css") ||
    ft.includes("css") ||
    ft.includes("text/javascript") ||
    ft.includes("application/javascript") ||
    ft.includes("application/x-javascript") ||
    ft.includes("js") ||
    ft.includes("javascript") ||
    ft.includes("text/markdown") ||
    ft.includes("text/x-markdown");

  if (isText && isReleased) return OAS365ICONS.textReleasedIcon;
  if (isText && isCheckedOut) return OAS365ICONS.textLockIcon;
  if (isText) return OAS365ICONS.textIcon;

  const audioExts = [
    "mp3",
    "wav",
    "aac",
    "ogg",
    "flac",
    "m4a",
    "wma",
    "opus",
    "webm",
    "amr",
    "3gp",
  ];
  const isAudio =
    ft.includes("audio") ||
    ft.includes("audio/mpeg") ||
    ft.includes("audio/mp3") ||
    ft.includes("mp3") ||
    ft.includes("audio/wav") ||
    ft.includes("audio/wave") ||
    ft.includes("audio/x-wav") ||
    ft.includes("wav") ||
    ft.includes("audio/aac") ||
    ft.includes("audio/x-aac") ||
    ft.includes("aac") ||
    ft.includes("audio/ogg") ||
    ft.includes("audio/vorbis") ||
    ft.includes("ogg") ||
    ft.includes("audio/flac") ||
    ft.includes("audio/x-flac") ||
    ft.includes("flac") ||
    ft.includes("audio/mp4") ||
    ft.includes("audio/x-m4a") ||
    ft.includes("m4a") ||
    ft.includes("audio/x-ms-wma") ||
    ft.includes("wma") ||
    ft.includes("audio/opus") ||
    ft.includes("opus") ||
    ft.includes("audio/webm") ||
    ft.includes("audio/amr") ||
    ft.includes("audio/3gpp") ||
    ft.startsWith("audio/") ||
    audioExts.includes(ext);

  if (isAudio && isReleased) return OAS365ICONS.audioReleasedIcon;
  if (isAudio && isCheckedOut) return OAS365ICONS.audioLockIcon;
  if (isAudio) return OAS365ICONS.audioFileIcon;

  const videoExts = [
    "mp4",
    "avi",
    "mov",
    "wmv",
    "flv",
    "webm",
    "mkv",
    "mpeg",
    "mpg",
    "3gp",
    "ogv",
  ];
  const isVideo =
    ft.includes("video") ||
    ft.includes("video/mp4") ||
    ft.includes("video/x-m4v") ||
    ft.includes("mp4") ||
    ft.includes("video/x-msvideo") ||
    ft.includes("avi") ||
    ft.includes("video/quicktime") ||
    ft.includes("mov") ||
    ft.includes("video/x-ms-wmv") ||
    ft.includes("wmv") ||
    ft.includes("video/x-flv") ||
    ft.includes("flv") ||
    ft.includes("video/webm") ||
    ft.includes("webm") ||
    ft.includes("video/x-matroska") ||
    ft.includes("mkv") ||
    ft.includes("video/mpeg") ||
    ft.includes("mpeg") ||
    ft.includes("mpg") ||
    ft.includes("video/3gpp") ||
    ft.includes("3gp") ||
    ft.includes("video/ogg") ||
    ft.includes("ogv") ||
    ft.startsWith("video/") ||
    videoExts.includes(ext);

  if (isVideo && isReleased) return OAS365ICONS.videoReleasedIcon;
  if (isVideo && isCheckedOut) return OAS365ICONS.videoLockIcon;
  if (isVideo) return OAS365ICONS.videoFileIcon;

  const archiveExts = ["zip", "rar", "7z", "tar", "gz", "bz2", "xz", "cab", "iso"];
  const isArchive =
    ft.includes("application/zip") ||
    ft.includes("application/x-zip-compressed") ||
    ft.includes("zip") ||
    ft.includes("compressed") ||
    ft.includes("application/x-rar-compressed") ||
    ft.includes("application/vnd.rar") ||
    ft.includes("rar") ||
    ft.includes("application/x-7z-compressed") ||
    ft.includes("7z") ||
    ft.includes("application/x-tar") ||
    ft.includes("tar") ||
    ft.includes("application/gzip") ||
    ft.includes("application/x-gzip") ||
    ft.includes("gz") ||
    ft.includes("application/x-bzip2") ||
    ft.includes("bz2") ||
    ft.includes("application/x-xz") ||
    ft.includes("xz") ||
    ft.includes("application/vnd.ms-cab-compressed") ||
    ft.includes("cab") ||
    ft.includes("application/x-iso9660-image") ||
    ft.includes("application/x-iso") ||
    ft.includes("iso") ||
    ft.includes("application/x-compress") ||
    ft.includes("application/x-compressed") ||
    ft.includes("application/archive") ||
    ft.includes("application/x-lzma") ||
    ft.includes("application/x-lzip") ||
    archiveExts.includes(ext);

  if (isArchive && isReleased) return OAS365ICONS.archiveReleasedIcon;
  if (isArchive && isCheckedOut) return OAS365ICONS.archiveLockIcon;
  if (isArchive) return OAS365ICONS.archive;

  return OAS365ICONS.notSupportedIcon;
};

export const generateFileIcon = (docType) => {
  const officeBrandedIcons = getOfficeBrandedIcons(docType);
  if (officeBrandedIcons !== "none") {
    return {
      docType,
      url: `https://static2.sharepointonline.com/files/fabric-cdn-prod_20200430.002/assets/brand-icons/product/svg/${officeBrandedIcons}_16x1.svg`,
    };
  } else {
    docType = getFileTypeVersion(docType);
    const iconIsSupported = findFileType(FILE_ICONS, docType);
    if (
      typeof iconIsSupported !== "undefined" &&
      iconIsSupported.name === docType
    ) {
      return {
        docType,
        url: `https://static2.sharepointonline.com/files/fabric/assets/item-types/16/${docType}.svg`,
      };
    } else {
      const _docType = "txt";
      return {
        docType,
        url: `https://static2.sharepointonline.com/files/fabric/assets/item-types/16/${_docType}.svg`,
      };
    }
  }
};

export const getFileType = (fileExt, fileTypeName) => {
  switch (fileExt.toLowerCase()) {
    case "doc":
    case "docx":
    case "docm":
      return "Word";
    case "xls":
    case "xlsx":
    case "xlsm":
      return "Excel";
    case "ppt":
    case "pptx":
    case "pptm":
      return "Powerpoint";
    case "msg":
    case "eml":
      return "Outlook";
    case "pdf":
      return "Pdf";
    case "txt":
      return "Text";
    case "image/jpeg":
    case "image/png":
    case "image/jpg":
    case "image/gif":
      return "Image";
    case "mp3":
      return "Audio";
    case "mp4":
    case "wmv":
      return "Video";
    case "application/rtf":
      return "rtf";
    default:
      return fileTypeName;
  }
};

export const capitalizeFirstLetter = (str) => {
  if (typeof str != "undefined" && str) {
    const capitalized = str?.charAt(0).toUpperCase() + str.slice(1);
    return capitalized.trim();
  }
  return "";
};

export const getETag = () => {
  return generateGUID();
};

export const getDocumentRevision = (docTitle) => {
  let rowSelectionDetails = [];

  if (docTitle !== undefined && docTitle !== null) {
    const matches = docTitle.match(/\(([^)]+)\)/g);

    if (matches && matches.length > 0) {
      const lastMatch = matches[matches.length - 1].slice(1, -1).trim();
      const pattern = /^(.+?)\s*-\s*(\d+)\s*-\s*(\d+)\s*-\s*(\w+)$/;
      const matchParts = lastMatch.match(pattern);

      if (matchParts && matchParts.length === 5) {
        const docRevision = {
          class: matchParts[1].trim(),
          docNo: matchParts[2].trim(),
          sheetNo: matchParts[3].trim(),
          docRev: matchParts[4].trim(),
        };
        rowSelectionDetails.push(docRevision);
      }
    }
  }

  return rowSelectionDetails;
};

export const isETagMacth = (eTagType, processingETag) => {
  let isMatch = true;
  let cachedETag = localStorage.getItem(eTagType) || "";
  console.log(`Cached ETag: ${cachedETag}`, "err");
  console.log(`Processing ETag: ${processingETag}`, "err");
  if (
    cachedETag !== undefined &&
    cachedETag !== null &&
    processingETag !== undefined &&
    processingETag !== null
  ) {
    if (cachedETag === processingETag) {
      isMatch = true;
    } else {
      isMatch = false;
    }
  } else {
    isMatch = false;
  }
  return isMatch;
};

export const getDefaultValue = (value, defaultValue) => {
  if (value !== undefined && value !== null) {
    return value;
  }
  return defaultValue;
};

export const getDefaultNumber = (value, defaultValue) => {
  if (value !== undefined && value !== null) {
    return Number(value);
  }
  return defaultValue;
};

function getOfficeBrandedIcons(fileExt) {
  switch (fileExt) {
    case "doc":
    case "docx":
    case "docm":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/msword":
    case "application/vnd.ms-word.document.macroEnabled.12":
      return "word";
    case "xls":
    case "xlsx":
    case "xlsm":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.ms-excel":
    case "application/vnd.ms-excel.sheet.macroEnabled.12":
      return "excel";
    case "ppt":
    case "pptx":
    case "pptm":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.ms-powerpoint":
    case "application/vnd.ms-powerpoint.presentation.macroEnabled.12":
      return "powerpoint";
    case "application/vnd.ms-outlook":
    case "msg":
    case "eml":
      return "outlook";
    case "accdb":
    case "mdb":
      return "access";
    case "one":
      return "onenote";
    default:
      return "none";
  }
}

function getFileTypeVersion(fileExt) {
  switch (fileExt) {
    case "doc":
    case "docm":
      return "docx";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/vnd.ms-word.document.macroEnabled.12":
    case "application/msword":
      return "docx";
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.ms-excel":
    case "application/vnd.ms-excel.sheet.macroEnabled.12":
      return "xlsx";
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.ms-powerpoint":
    case "application/vnd.ms-powerpoint.presentation.macroEnabled.12":
      return "pptx";
    case "application/pdf":
      return "pdf";
    case "xls":
    case "xlsm":
      return "xlsx";
    case "ppt":
      return "pptx";
    case "image/jpeg":
    case "image/png":
    case "image/jpg":
    case "image/gif":
    case "image/tiff":
    case "image/tif":
      return "photo";
    case "application/rtf":
      return "rtf";
    case "application/octet-stream":
    case "message/rfc822":
    case "application/vnd.ms-outlook":
    case "application/vnd.ms-outlook.msg":
      return "email";
    case "text/html":
      return "html";
    case "text/csv":
      return "csv";
    case "application/json":
      return "code";
    default:
      return fileExt;
  }
}

function findFileType(array, fileExt) {
  return array.find((element) => {
    return element.name === fileExt;
  });
}

const FILE_ICONS = [
  { name: "accdb" },
  { name: "audio" },
  { name: "code" },
  { name: "csv" },
  { name: "docx" },
  { name: "dotx" },
  { name: "mpp" },
  { name: "mpt" },
  { name: "model" },
  { name: "one" },
  { name: "onetoc" },
  { name: "potx" },
  { name: "ppsx" },
  { name: "pdf" },
  { name: "photo" },
  { name: "pptx" },
  { name: "presentation" },
  { name: "potx" },
  { name: "pub" },
  { name: "rtf" },
  { name: "spreadsheet" },
  { name: "txt" },
  { name: "vector" },
  { name: "vsdx" },
  { name: "vssx" },
  { name: "vstx" },
  { name: "xlsx" },
  { name: "xltx" },
  { name: "xsn" },
  { name: "email" },
  { name: "html" },
];

function _generateFileSize(bytes, decimals = 2) {
  if (bytes === null) return "N/A";
  else if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
}

function generateGUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}
